import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface KpiBlock {
  readonly metrics: Record<string, { value: number; target: number | null; isBreach: boolean; metricDate: string }>;
  readonly breachCount: number;
  readonly metricCount: number;
}
interface GovernanceBlock {
  readonly verdict: 'COMPLIANT' | 'NON-COMPLIANT';
  readonly demand: { total: number; approved: number; strategicApproved: number; majorWithoutApprovedBusinessCase: number };
  readonly requirement: { total: number; approvedOrBaseline: number; approvedNotTraceable: number };
  readonly roadmap: { total: number; published: number; publishedWithoutExecutiveApproval: number };
  readonly portfolio: { total: number; active: number; reviewOverdue: number };
  readonly benefit: { total: number; measured: number; measurementOverdue: number };
  readonly gaps: readonly string[];
}
interface Totals {
  readonly demands: number;
  readonly ideas: number;
  readonly requirements: number;
  readonly features: number;
  readonly backlogItems: number;
  readonly businessCases: number;
  readonly roadmaps: number;
  readonly portfolios: number;
  readonly benefits: number;
  readonly innovations: number;
}
interface ExecutiveDashboard {
  readonly kpis: KpiBlock;
  readonly governance: GovernanceBlock;
  readonly pipeline: Record<string, Record<string, number>>;
  readonly totals: Totals;
}

/**
 * M33 — Executive PMO Dashboard (§31 / §23). Read-only executive rollup: the
 * §21 governance COMPLIANT / NON-COMPLIANT verdict, the §22 analytics KPI
 * snapshot (breach count), the cross-domain portfolio totals and the enumerated
 * governance gaps (BR-01..BR-07) that must be zero for sign-off. Requires
 * EXECUTIVE_VIEW.
 */
export function ExecutivePmoDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<ExecutiveDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: ExecutiveDashboard }>('/v1/pm/dashboard', { token })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="executive-pmo-dashboard">
      <h1>{t('m33.exec.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !data ? (
        <p>{t('m33.common.loading')}</p>
      ) : (
        <>
          <section aria-label="verdict" data-verdict={data.governance.verdict}>
            <p aria-label="verdict-value">{data.governance.verdict}</p>
            <p aria-label="breach-count">{t('m33.exec.breaches', { n: data.kpis.breachCount })}</p>
            <p aria-label="metric-count">{t('m33.exec.metricsTracked', { n: data.kpis.metricCount })}</p>
          </section>
          <section aria-label="totals" className="card-grid">
            <article aria-label="card-demands"><h2>{t('m33.exec.cardDemands')}</h2><p aria-label="total-demands">{data.totals.demands}</p></article>
            <article aria-label="card-ideas"><h2>{t('m33.exec.cardIdeas')}</h2><p aria-label="total-ideas">{data.totals.ideas}</p></article>
            <article aria-label="card-requirements"><h2>{t('m33.exec.cardRequirements')}</h2><p aria-label="total-requirements">{data.totals.requirements}</p></article>
            <article aria-label="card-features"><h2>{t('m33.exec.cardFeatures')}</h2><p aria-label="total-features">{data.totals.features}</p></article>
            <article aria-label="card-backlog"><h2>{t('m33.exec.cardBacklog')}</h2><p aria-label="total-backlog">{data.totals.backlogItems}</p></article>
            <article aria-label="card-business-cases"><h2>{t('m33.exec.cardBusinessCases')}</h2><p aria-label="total-business-cases">{data.totals.businessCases}</p></article>
            <article aria-label="card-roadmaps"><h2>{t('m33.exec.cardRoadmaps')}</h2><p aria-label="total-roadmaps">{data.totals.roadmaps}</p></article>
            <article aria-label="card-portfolios"><h2>{t('m33.exec.cardPortfolios')}</h2><p aria-label="total-portfolios">{data.totals.portfolios}</p></article>
            <article aria-label="card-benefits"><h2>{t('m33.exec.cardBenefits')}</h2><p aria-label="total-benefits">{data.totals.benefits}</p></article>
            <article aria-label="card-innovations"><h2>{t('m33.exec.cardInnovations')}</h2><p aria-label="total-innovations">{data.totals.innovations}</p></article>
          </section>
          <section aria-label="governance-detail">
            <h2>{t('m33.exec.govPosture')}</h2>
            <p aria-label="gov-major-missing">{t('m33.exec.govMajorMissing', { n: data.governance.demand.majorWithoutApprovedBusinessCase })}</p>
            <p aria-label="gov-not-traceable">{t('m33.exec.govNotTraceable', { n: data.governance.requirement.approvedNotTraceable })}</p>
            <p aria-label="gov-roadmap-unapproved">{t('m33.exec.govRoadmapUnapproved', { n: data.governance.roadmap.publishedWithoutExecutiveApproval })}</p>
            <p aria-label="gov-portfolio-overdue">{t('m33.exec.govPortfolioOverdue', { n: data.governance.portfolio.reviewOverdue })}</p>
            <p aria-label="gov-benefit-overdue">{t('m33.exec.govBenefitOverdue', { n: data.governance.benefit.measurementOverdue })}</p>
          </section>
          <section aria-label="gaps">
            <h2>{t('m33.exec.govGaps')}</h2>
            <p aria-label="gap-count">{t('m33.exec.gapCount', { n: data.governance.gaps.length })}</p>
            {data.governance.gaps.length === 0 ? (
              <p aria-label="no-gaps">{t('m33.exec.noGaps')}</p>
            ) : (
              <ul aria-label="gap-list">
                {data.governance.gaps.map((g) => <li key={g}>{g}</li>)}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}
